import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';

function AdminAds() {
    const [stats, setStats] = useState([]);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState(null);
    const { user, refreshUser } = useUser();

    const [loading, setLoading] = useState(false);

    const fetchStats = async () => {
        try {
            setError('');
            setDebugInfo(null);
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = token ? { 'x-auth-token': token } : {};
            const resp = await fetch('/api/ads/stats', { headers });
            if (!resp.ok) {
                // handle common auth cases with clearer messages
                if (resp.status === 401) {
                    setError('Authentication required. Please sign in.');
                    return;
                }
                if (resp.status === 403) {
                    setError('Admin access required. If you were recently granted admin, click "Refresh profile".');

                    // Gather debug information to help diagnose why the server is rejecting admin
                    try {
                        const meResp = await fetch('/api/auth/me', { headers });
                        const meBody = await meResp.text().catch(() => null);
                        const parsedMe = tryParseJsonSafe(meBody);

                        // Also attempt to read server 403 response body for debug (may include debug object)
                        let serverBody = null;
                        try {
                            serverBody = await resp.json().catch(() => null);
                        } catch (e) {
                            serverBody = null;
                        }

                        setDebugInfo({ tokenPresent: !!token, tokenSample: token ? token.slice(0,8) + '...' : null, meStatus: meResp.status, meBody: parsedMe, server403: serverBody });
                    } catch (dbgErr) {
                        setDebugInfo({ tokenPresent: !!token, tokenSample: token ? token.slice(0,8) + '...' : null, meError: dbgErr.message });
                    }

                    return;
                }
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to fetch stats');
            }
            const data = await resp.json();
            setStats(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to safely parse JSON if possible
    const tryParseJsonSafe = (s) => {
        try {
            return JSON.parse(s);
        } catch (e) {
            return s;
        }
    };

    useEffect(() => { fetchStats(); }, [user]);

    const exportCsv = () => {
        if (!stats || stats.length === 0) return;
        const rows = [['date','impressions','clicks'], ...stats.map(r=>[r.date, r.impressions||0, r.clicks||0])];
        const csv = rows.map(r=>r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'ads-stats.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    };

    const handleRefresh = async () => {
        // Refresh profile then retry
        if (typeof refreshUser === 'function') {
            await refreshUser();
        }
        fetchStats();
    };

    return (
        <div className="page admin-ads-page">
            <header className="page-header">
                <span className="page-eyebrow">Admin</span>
                <h1>Ad analytics (last 30 days)</h1>
                <p className="page-subtitle">Simple impressions and clicks per day. Use the API or script to export data.</p>
            </header>

            {error && (
                <div className="alert alert-danger">
                    {error}
                    {error.includes('Admin access') && (
                        <div className="mt-2">
                            <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleRefresh}>Refresh profile</button>
                            <span className="small text-muted">If you were just granted admin, this will update your profile and retry.</span>
                        </div>
                    )}
                    {debugInfo && (
                        <div className="mt-2 alert alert-secondary small">
                            <strong>Debug:</strong>
                            <pre style={{whiteSpace:'pre-wrap', maxHeight:200, overflow:'auto'}}>{JSON.stringify(debugInfo, null, 2)}</pre>
                        </div>
                    )}
                </div>
            )}

            <div className="admin-actions">
                <button className="btn btn-outline-secondary" onClick={handleRefresh} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
                <button className="btn btn-primary" onClick={exportCsv} disabled={loading || stats.length===0}>Export CSV</button>
                <div style={{marginLeft:'auto'}} className="text-muted small">Showing last {stats.length} days</div>
            </div>

            <section className="panel mt-2">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Impressions</th>
                            <th>Clicks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.length === 0 ? (
                            <tr><td colSpan="3">No data</td></tr>
                        ) : (
                            stats.map(row => (
                                <tr key={row.date}>
                                    <td>{row.date}</td>
                                    <td>{row.impressions || 0}</td>
                                    <td>{row.clicks || 0}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </section>

            <p className="text-muted small mt-3">To secure this page, set ADMIN_EMAILS (comma-separated) or use ADMIN_TOKEN for scripted access.</p>
        </div>
    );
}

export default AdminAds;
